import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { PlanSubmitBlockReason } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

import { formatDateTimeFull } from '~/models/dateTime'
import { submitBlockReasonKey } from '~/models/planSubmission/submitBlockReason'
import { blockReasonView } from '~/pages/ProcurementPlanDetail/models/blockReasonView'

/**
 * 需要調達計画詳細画面の提出ボタン活性判定 (AC-02 / AC-06 / AC-07 / AC-08)。
 *
 * 活性 (提出可): GENERATED (未提出) / SUBMIT_FAILED (提出失敗、同一版
 * re-enqueue) / SEND_FAILED (送信失敗、手動再提出) / REJECTED (差戻し、同一版 in-place 再提出) / INCONSISTENT
 * (不整合、committed path で新版 mint 提出)。
 *
 * 非活性: GENERATING / GENERATION_FAILED (AC-08: 作成中・作成失敗は XML
 * 未生成のため提出不可。作成失敗の復旧は再作成経由) / SUBMITTING (AC-06:
 * 処理中への再提出は拒否。backend の status gate と対称) / SUBMITTED /
 * ACCEPTED (提出済・受領済。変更は再作成経由) / EDITING (未保存編集あり。
 * 本画面は read-only のため実際には到達しない想定だが防御的に非活性)。
 *
 * backend の提出可否ゲート (WithSubmitting の explicit allowlist /
 * NewGeneration の Inconsistent 受理) と対応する frontend 側の事前判定。
 */
const SUBMITTABLE_STATUSES: ReadonlySet<PlanSubmissionStatus> = new Set([
  PlanSubmissionStatus.GENERATED,
  PlanSubmissionStatus.SUBMIT_FAILED,
  PlanSubmissionStatus.SEND_FAILED,
  PlanSubmissionStatus.REJECTED,
  PlanSubmissionStatus.INCONSISTENT,
])

export function canSubmit(status: PlanSubmissionStatus): boolean {
  return SUBMITTABLE_STATUSES.has(status)
}

/**
 * 画面上の提出操作 (ヘッダーの「提出」 と、送信失敗の誘導メッセージの「再提出」)
 * を押せるか。status の判定 (`canSubmit`) に加え、提出中・未入力コマあり
 * (AC-PLC-028)・閲覧のみ (workflow-back-bar-readonly AC-22)・提出を止める理由がある版
 * (AC-PLC-055 / 056 / 062。 未知の理由も含め UNSPECIFIED 以外は押させない)・OCCTO の受付時間外
 * (AC-PLC-062。 backend が開いた時点で判定した `directSubmitHeldUntil` がある) の時は押させない。
 * 2 つのボタンが同じ条件で無効になるよう (AC-PLC-020-2 ※)、判定をここに 1 つ置く。
 */
function isSubmitActionEnabled(input: {
  status: PlanSubmissionStatus
  isSubmitting: boolean
  hasEmptySlots: boolean
  isReadonly: boolean
  submitBlockReason: PlanSubmitBlockReason
  directSubmitHeldUntil: Date | undefined
}): boolean {
  return (
    canSubmit(input.status) &&
    input.submitBlockReason === PlanSubmitBlockReason.UNSPECIFIED &&
    input.directSubmitHeldUntil === undefined &&
    !input.isSubmitting &&
    !input.hasEmptySlots &&
    !input.isReadonly
  )
}

/** 押せない理由の文の i18n key と、 その文に埋める値。 */
export type SubmitDisabledReason = {
  key: string
  values?: Record<string, string>
}

/**
 * 提出操作を押せない理由の文 (AC-PLC-062 / AC-PLC-028)。押せる時・理由の文が無い
 * 無効 (提出中・閲覧のみ) は undefined。閲覧のみ (AC-WFW-014) の理由は文言を持つ
 * `useWorkflowLockReasons` が決め、呼び出し側がこの関数の結果より優先して出す。状態の理由が先、次に取引先の行の欠け (056)、最後に
 * 未入力のコマ、最後に OCCTO の受付時間外 (AC-PLC-062)。056 は作り直すまで直らない版の不備で、未入力のコマより先に案内する。
 * 受付時間外は待てば押せるので、待っても直らない版の不備より後に出す。
 * ヘッダーの「提出」 と誘導メッセージの「提出する」「再提出」 が同じ理由を出す。
 * `isDataDefect` は、返した理由が版のデータの不備 (056・未入力のコマ) による時だけ true。
 */
function resolveSubmitDisabledReason(input: {
  canSubmitNow: boolean
  status: PlanSubmissionStatus
  submitBlockReason: PlanSubmitBlockReason
  hasEmptySlots: boolean
  directSubmitHeldUntil: Date | undefined
}): { reason: SubmitDisabledReason | undefined; isDataDefect: boolean } {
  if (input.canSubmitNow) return { reason: undefined, isDataDefect: false }
  // #9688 Des 決定: 作成失敗で計画値が無いことを、元データの未入力と案内しない。
  if (input.status === PlanSubmissionStatus.GENERATION_FAILED) {
    return {
      reason: {
        key: 'page:procurementPlanDetail.submit.generationFailedTooltip',
      },
      isDataDefect: false,
    }
  }
  const statusReasonKey = submitBlockReasonKey(input.status)
  if (statusReasonKey !== undefined) {
    return { reason: { key: statusReasonKey }, isDataDefect: false }
  }
  const blockView = blockReasonView(input.submitBlockReason)
  if (blockView?.submitButton === 'disabled') {
    return { reason: { key: blockView.messageKey }, isDataDefect: true }
  }
  if (input.hasEmptySlots) {
    return {
      reason: { key: 'page:procurementPlanDetail.submit.emptySlotTooltip' },
      isDataDefect: true,
    }
  }
  if (input.directSubmitHeldUntil !== undefined) {
    return {
      reason: {
        key: DIRECT_SUBMIT_HELD_REASON_KEY,
        values: { resumesAt: formatDateTimeFull(input.directSubmitHeldUntil) },
      },
      isDataDefect: false,
    }
  }
  return { reason: undefined, isDataDefect: false }
}

/** OCCTO の受付時間外 (前日 12:00〜17:00) で提出を止めている時の理由の文の key (AC-PLC-062)。 */
export const DIRECT_SUBMIT_HELD_REASON_KEY =
  'page:procurementPlanDetail.submit.directSubmitHeldTooltip'

/** 画面上の提出操作を押せるかと、 押せない時に文で出す理由。 */
export type SubmitAvailability = {
  enabled: boolean
  disabledReason: SubmitDisabledReason | undefined
  /** `disabledReason` が版のデータの不備 (056・未入力のコマ) による理由か。 */
  disabledReasonIsDataDefect: boolean
}

/**
 * ヘッダーの「提出」 と誘導メッセージの「提出する」「再提出」 が同じ結論を出すよう
 * (AC-PLC-020-2 ※ / AC-PLC-062)、 詳細画面はこの値を 1 回だけ求めて両方に渡す。
 */
export function resolveSubmitAvailability(input: {
  status: PlanSubmissionStatus
  isSubmitting: boolean
  hasEmptySlots: boolean
  isReadonly: boolean
  submitBlockReason: PlanSubmitBlockReason
  /**
   * OCCTO が受け付けない時間帯の終わり (AC-PLC-062。 backend が詳細を返した時点で判定した値)。
   * ある間は押させない。 画面の時計とは比べない (開いたまま時刻を跨いだ時は AC-PLC-085 が受ける)。
   */
  directSubmitHeldUntil: Date | undefined
}): SubmitAvailability {
  const enabled = isSubmitActionEnabled(input)
  const reason = resolveSubmitDisabledReason({
    canSubmitNow: enabled,
    status: input.status,
    submitBlockReason: input.submitBlockReason,
    hasEmptySlots: input.hasEmptySlots,
    directSubmitHeldUntil: input.directSubmitHeldUntil,
  })
  return {
    enabled,
    disabledReason: reason.reason,
    disabledReasonIsDataDefect: reason.isDataDefect,
  }
}
